import { useEffect } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './HelpDrawer.css';

const HELP_DRAWER_CLOSE_LABEL = 'Close help';
const HELP_DRAWER_ERROR_TITLE = 'Help is temporarily unavailable';
const HELP_DRAWER_ERROR_MESSAGE = 'We could not load the documentation for this page right now.';
const HELP_DRAWER_RETRY_LABEL = 'Retry';
const HELP_DRAWER_EMPTY_TITLE = 'No help available';
const HELP_DRAWER_EMPTY_MESSAGE = 'Documentation for this page is coming soon.';
const HELP_DRAWER_EMPTY_ICON_CLASS_NAME = 'bi bi-journal-text';
const HELP_DRAWER_ERROR_ICON_CLASS_NAME = 'bi bi-exclamation-circle';
const HELP_DRAWER_RETRY_DELAY_MS = 0;
const HELP_DRAWER_TITLE_ID = 'page-help-drawer-title';

export interface HelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry?: () => void;
  title: string;
  content: string;
  loading: boolean;
  error: boolean;
}


/**
 * Displays contextual help content inside a route-aware slide-in drawer.
 *
 * @param props.isOpen Controls the drawer visibility.
 * @param props.onClose Closes the drawer and backdrop.
 * @param props.onRetry Re-triggers the page help request after a failed load.
 * @param props.title Heading displayed in the drawer header.
 * @param props.content Markdown content to render for the current page.
 * @param props.loading Shows the loading skeleton while help content is fetched.
 * @param props.error Shows the retry state when the request fails.
 */
export default function HelpDrawer({ isOpen, onClose, onRetry, title, content, loading, error }: HelpDrawerProps) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const hasContent = content.trim().length > 0;

  const handleRetry = () => {
    onClose();

    if (onRetry) {
      window.setTimeout(onRetry, HELP_DRAWER_RETRY_DELAY_MS);
    }
  };

  return (
    <>
      <div className={`help-drawer__backdrop${isOpen ? ' help-drawer__backdrop--open' : ''}`} onClick={onClose} aria-hidden={!isOpen} />
      <aside
        className={`help-drawer${isOpen ? ' help-drawer--open' : ''}`}
        role='dialog'
        aria-modal='true'
        aria-hidden={!isOpen}
        aria-labelledby={HELP_DRAWER_TITLE_ID}
      >
        <div className='help-drawer__header'>
          <h2 className='help-drawer__title' id={HELP_DRAWER_TITLE_ID}>
            {title}
          </h2>
          <button type='button' className='help-drawer__close' aria-label={HELP_DRAWER_CLOSE_LABEL} onClick={onClose}>
            <span aria-hidden='true'>&times;</span>
          </button>
        </div>
        <div className='help-drawer__body'>
          {loading ? (
            <div className='help-drawer__skeleton' aria-hidden='true'>
              <div className='help-drawer__skeleton-bar help-drawer__skeleton-bar--wide' />
              <div className='help-drawer__skeleton-bar help-drawer__skeleton-bar--medium' />
              <div className='help-drawer__skeleton-bar help-drawer__skeleton-bar--short' />
            </div>
          ) : error ? (
            <div className='help-drawer__state'>
              <div className='help-drawer__state-icon'>
                <i className={HELP_DRAWER_ERROR_ICON_CLASS_NAME} aria-hidden='true' />
              </div>
              <h3 className='help-drawer__state-title'>{HELP_DRAWER_ERROR_TITLE}</h3>
              <p className='help-drawer__state-message'>{HELP_DRAWER_ERROR_MESSAGE}</p>
              <button type='button' className='help-drawer__action' onClick={handleRetry}>
                {HELP_DRAWER_RETRY_LABEL}
              </button>
            </div>
          ) : hasContent ? (
            <div className='help-drawer__markdown'>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            </div>
          ) : (
            <div className='help-drawer__state'>
              <div className='help-drawer__state-icon'>
                <i className={HELP_DRAWER_EMPTY_ICON_CLASS_NAME} aria-hidden='true' />
              </div>
              <h3 className='help-drawer__state-title'>{HELP_DRAWER_EMPTY_TITLE}</h3>
              <p className='help-drawer__state-message'>{HELP_DRAWER_EMPTY_MESSAGE}</p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
